import React, { RefObject, useCallback, useMemo } from 'react'
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import PokerOrderIcon from '../../../assets/LearnScreen/poker-order.svg'
import { DS } from '../../design-system'
import { translate } from '../../i18n'
import { ProgressBar } from '../ANewDesign/ProgressBar'
import { ReduceMotion } from 'react-native-reanimated'
import { useMaxWidth, isWeb } from '../WebWrapper'

type CompletedChapterSheetProps = {
  bottomSheetModalRef: RefObject<BottomSheetModal | null>
  okayPress: () => void
  ignorePress: () => void
  chapterName: string | undefined
  chapterNumber: number | undefined
  nextChapterName: string | undefined
  isLessonCompleted: () => boolean
  isAllLessonCompleted: () => boolean
  onComplete: () => void
}

export const CompletedChapterSheet = ({
  bottomSheetModalRef,
  ignorePress,
  okayPress,
  chapterName,
  chapterNumber = 1,
  nextChapterName = '',
  isLessonCompleted,
  isAllLessonCompleted,
  onComplete
}: CompletedChapterSheetProps) => {
  const maxWidth = useMaxWidth()
  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />
    ),
    []
  )

  const percentage = Platform.OS === 'ios' ? '47%' : '54%'

  const snapPoints = useMemo(() => [percentage], [percentage])
  const duration = 5 * 1000

  const showSubtitle = () => {
    if (isAllLessonCompleted()) {
      return translate('completedChapter.subtitleAllLessons')
    }
    if (isLessonCompleted()) {
      return `${translate('completedChapter.subtitleLesson')} ${chapterName}`
    }

    return `${translate('completedChapter.subtitleChapter')} ${chapterNumber}`
  }

  return (
    <View>
      <BottomSheetModal
        ref={bottomSheetModalRef}
        index={0}
        snapPoints={snapPoints}
        backdropComponent={renderBackdrop}
        detached={false}
        handleStyle={styles.bottomSheetHandleStyle}
        handleIndicatorStyle={styles.bottomSheetIndicatorStyle}
        keyboardBlurBehavior="restore"
        overrideReduceMotion={ReduceMotion.System}
        style={{
          marginHorizontal: isWeb ? 'auto' : undefined,
          maxWidth: maxWidth,
          width: '100%'
        }}
      >
        <BottomSheetView style={{ flex: 1 }}>
          <DS.Frame style={styles.container}>
            <DS.Frame>
              <DS.Frame style={{ alignSelf: 'center' }}>
                <DS.Icon name="PokerOrder" size={48} />
              </DS.Frame>
              <DS.Text variant="Heading 2/Medium" textAlign="center" style={{ marginTop: 8 }}>
                {translate('completedChapter.title')}
              </DS.Text>
              <DS.Text variant="Body Large/Regular" textAlign="center" style={{ marginTop: 8 }}>
                {showSubtitle()}
              </DS.Text>
            </DS.Frame>

            {!isAllLessonCompleted() ? (
              <>
                <DS.Frame style={styles.autoplaying} row alignItems="center" gap="12">
                  <DS.Frame>
                    <DS.Icon name="Play--black" size={40} color="Global/Black" />
                  </DS.Frame>
                  <DS.Frame alignItems="flex-start">
                    <DS.Text variant="Body Medium/Regular" textAlign="center">
                      {isLessonCompleted()
                        ? translate('completedChapter.autoplayingNextLesson')
                        : translate('completedChapter.autoplaying')}
                    </DS.Text>
                    <DS.Text variant="Body Medium/Medium" textAlign="center">
                      {isLessonCompleted() ? '' : nextChapterName}
                    </DS.Text>
                  </DS.Frame>
                </DS.Frame>
                <DS.Frame style={styles.progressBar}>
                  <ProgressBar
                    height={5}
                    duration={duration}
                    maxDuration={duration}
                    backgroundColor={'Global/Black'}
                    backgroundOpacity={0.1}
                    color={'Brand/Orange'}
                    onComplete={onComplete}
                  />
                </DS.Frame>
              </>
            ) : null}

            <DS.Frame style={styles.actionsContainer}>
              <TouchableOpacity style={styles.okayButton} onPress={okayPress}>
                <DS.Text variant="Body Medium/Medium" color="Global/White" textAlign="center">
                  {!isAllLessonCompleted()
                    ? translate('completedChapter.watchNow')
                    : translate('completedChapter.returnTo')}
                </DS.Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ignoreButton} onPress={ignorePress}>
                <DS.Text variant="Body Medium/Medium" color="Global/Black" textAlign="center">
                  {translate('completedChapter.cancel')}
                </DS.Text>
              </TouchableOpacity>
            </DS.Frame>
          </DS.Frame>
        </BottomSheetView>
      </BottomSheetModal>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F6F2E9',
    paddingHorizontal: 32
  },
  autoplaying: {
    marginVertical: 12,
    paddingVertical: 12,
    borderTopColor: '#DDDAD2',
    borderTopWidth: 1
  },
  progressBar: {},
  bottomSheetHandleStyle: {
    backgroundColor: '#F6F2E9',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 0,
    height: 24
  },
  bottomSheetIndicatorStyle: {
    display: 'none'
  },
  okayButton: {
    paddingHorizontal: DS.spacing[24],
    paddingVertical: 12.5,
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: DS.spacing[24],
    width: '100%',
    height: 48
  },
  ignoreButton: {
    paddingHorizontal: DS.spacing[24],
    paddingVertical: 12.5,
    borderRadius: DS.spacing[24],
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    width: '100%',
    height: 48
  },
  actionsContainer: {
    flex: 1,
    paddingTop: 16,
    gap: 6
  }
})
